import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import {
  IS_PRO,
  REGION,
  RegionEnum,
} from 'gatsby-theme-hem-store/src/constants';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import toString from 'lodash/toString';
import React from 'react';

type Props = {
  title: string;
  desc?: string;
  banner?: string;
  type: 'product' | 'website';
  price?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  reviews?: Queries.VariantPageQuery['reviews']['nodes'];
  inStock?: boolean;
  shipping?: number;
  crumbs?:
    | {
        title: string;
        slug: string;
      }[]
    | boolean;
  productName?: string;
  productImages?: readonly {
    readonly url: string;
    readonly gatsbyImageData: IGatsbyImageData;
  }[];
};

const withSchemaImageParams = (url: string): string => {
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams + '?w=960&h=960&q=80&fm=jpg&fit=pad';
};

const withOgImageParams = (url: string): string => {
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams + '?w=1200&h=630&q=80&fm=jpg&fit=pad';
};

const withOgImageParamsNotProduct = (url: string): string => {
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams + '?w=1200&h=630&q=80&fm=jpg&&fit=fill';
};

const withTwitterImageParams = (url: string): string => {
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams + '?w=800&h=418&q=80&fm=jpg&fit=pad';
};

const withTwitterImageParamsNotProduct = (url: string): string => {
  const urlWithoutParams = url.split('?')[0];
  return urlWithoutParams + '?w=800&h=418&q=80&fm=jpg&&fit=fill';
};

const region = `en-${process.env.GATSBY_REGION}`.toLowerCase();

export const SEO: React.FC<Props> = ({
  title,
  desc,
  banner,
  type,
  price,
  noIndex,
  noFollow,
  reviews,
  inStock,
  shipping,
  crumbs = false,
  productName,
  productImages,
}): JSX.Element => {
  const { pathname } = useLocation();
  const { site } = useStaticQuery<Queries.SEOQuery>(graphql`
    query SEO {
      site {
        siteMetadata {
          siteUrl
          defaultTitle: title
          defaultDescription: description
          defaultBanner: banner
          headline
          siteLanguage
          ogLanguage
          author
          currency
        }
      }
    }
  `);

  const {
    siteMetadata: {
      siteUrl,
      defaultTitle,
      defaultDescription,
      defaultBanner,
      headline,
      siteLanguage,
      ogLanguage,
      author,
      currency,
    },
  } = site;

  const seo = {
    title: title ? title + ' — ' + defaultTitle : defaultTitle,
    description: desc || defaultDescription,
    image: (banner || defaultBanner).startsWith('https')
      ? banner || defaultBanner
      : `${siteUrl}${banner || defaultBanner}`,
    url: `${siteUrl}${pathname}`.replace(`${region}/${region}`, region),
  };

  // schema.org in JSONLD format
  // https://developers.google.com/search/docs/guides/intro-structured-data
  // You can fill out the 'author', 'creator' with more data or another type (e.g. 'Organization')

  let breadcrumb;
  let schemaProduct;
  let schemaOrgWebPage;

  if (type === 'website') {
    schemaOrgWebPage = {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      url: siteUrl,
      headline,
      inLanguage: siteLanguage,
      mainEntityOfPage: siteUrl,
      description: desc,
      name: defaultTitle,
      author: {
        '@type': 'Organization',
        name: author,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}${defaultBanner}`,
        },
      },
      image: {
        '@type': 'ImageObject',
        url: withOgImageParams(seo.image),
      },
    };

    breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 0,
          item: {
            '@id': `${siteUrl}/${region}`,
            name: 'Home',
          },
        },
        ...pathname.split('/').map((crumb, index) => {
          if (index === 0) return null;
          const previous = pathname.split('/').slice(0, index);

          return {
            '@type': 'ListItem',
            position: index,
            item: {
              '@id': [siteUrl, region, ...previous, crumb]
                .filter(Boolean)
                .join('/'),
              name:
                crumb.charAt(0).toUpperCase() +
                crumb.replace(/-/g, ' ').slice(1).toLowerCase(),
            },
          };
        }),
      ].filter(Boolean),
    };
  }

  if (type === 'product') {
    schemaProduct = {
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: seo.title,
      description: seo.description,
      image: isArray(productImages)
        ? productImages.map((image) => withSchemaImageParams(image.url))
        : withSchemaImageParams(seo.image),
      // conditionally include reviews
      ...(isArray(reviews) &&
        reviews.length > 0 && {
          review: reviews.map((review) => ({
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: toString(review.score),
            },
            author: {
              '@type': 'Person',
              name: review.name,
            },
          })),
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue:
              reviews.reduce((a, b) => a + b.score, 0) / reviews.length,
            bestRating: 5,
            ratingCount: reviews.length,
            reviewCount: reviews.length,
          },
        }),
      brand: {
        '@type': 'Brand',
        name: 'Hem',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}${defaultBanner}`,
        },
      },
      offers: {
        '@type': 'Offer',
        availability: inStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        price: isString(price) ? price.replace(',', '.') : price,
        priceCurrency: currency,
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: author,
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}${defaultBanner}`,
          },
        },
        ...(!IS_PRO &&
          !!shipping &&
          REGION === RegionEnum.Us && {
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingRate: {
                '@type': 'MonetaryAmount',
                value: shipping + '.00',
                currency,
              },
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'US',
              },
            },
          }),
      },
      sku: pathname.split('/').pop(),
    };

    if (isArray(crumbs)) {
      breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 0,
            item: {
              '@id': `${siteUrl}/${region}`,
              name: 'Home',
            },
          },
          ...crumbs.map((crumb, index) => {
            if (!crumb.slug) {
              return [
                {
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    '@id': `${siteUrl}/${region}${pathname}`
                      .split('/')
                      .slice(0, -1)
                      .join('/'),
                    name: productName,
                  },
                },
                {
                  '@type': 'ListItem',
                  position: index + 2,
                  item: {
                    '@id': `${siteUrl}/${region}${pathname}`,
                    name: title,
                  },
                },
              ];
            }

            return {
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': `${siteUrl}/${region}${crumb.slug}`,
                name: crumb.title,
              },
            };
          }),
        ].flat(),
      };
    }
  }

  return (
    <>
      <title>{seo.title}</title>

      {region !== 'en-eu' ? (
        <link
          rel="alternate"
          hrefLang="eu"
          href={seo.url.replace(region, 'en-eu')}
        />
      ) : null}
      {region !== 'en-us' ? (
        <link
          rel="alternate"
          hrefLang="en-us"
          href={seo.url.replace(region, 'en-us')}
        />
      ) : null}
      {region !== 'en-gb' && IS_PRO ? (
        <link
          rel="alternate"
          hrefLang="en-gb"
          href={seo.url.replace(region, 'en-gb')}
        />
      ) : null}
      {region !== 'en-ca' && !IS_PRO ? (
        <link
          rel="alternate"
          hrefLang="en-ca"
          href={seo.url.replace(region, 'en-ca')}
        />
      ) : null}

      <meta charSet="utf-8" />
      <meta name="description" content={seo.description} />
      <meta
        name="image"
        content={
          type === 'product'
            ? withOgImageParams(seo.image)
            : withOgImageParamsNotProduct(seo.image)
        }
      />
      <link rel="canonical" href={seo.url} />

      {type === 'website' && (
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgWebPage)}
        </script>
      )}

      {type === 'product' && (
        <script type="application/ld+json">
          {JSON.stringify(schemaProduct)}
        </script>
      )}

      {!!crumbs && isArray(breadcrumb.itemListElement) && (
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      )}

      {/* facebook */}
      <meta property="og:site_name" content="Hem" />
      <meta property="og:locale" content={ogLanguage} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:site_name" content="Hem" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta
        property="og:image"
        content={
          type === 'product'
            ? withOgImageParams(seo.image)
            : withOgImageParamsNotProduct(seo.image)
        }
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {!!price && <meta property="og:price:amount" content={price} />}
      {!!currency && <meta property="og:price:currency" content={currency} />}

      {/* twitter */}
      <meta name="twitter:site" content="@hem" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta
        name="twitter:image"
        content={
          type === 'product'
            ? withTwitterImageParams(seo.image)
            : withTwitterImageParamsNotProduct(seo.image)
        }
      />

      {/* robots */}
      {noIndex ||
      process.env.GATSBY_REGION_REGION_DIFFERENTIATOR.includes('DEV') ||
      process.env.GATBSY_BLOCK_SEARCH_ENGINES === 'true' ? (
        <meta name="robots" content="noindex" />
      ) : null}

      {noFollow ||
      process.env.GATSBY_REGION_REGION_DIFFERENTIATOR.includes('DEV') ||
      process.env.GATBSY_BLOCK_SEARCH_ENGINES === 'true' ? (
        <meta name="robots" content="nofollow" />
      ) : null}
    </>
  );
};
